import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { broadcastAuth, onAuthMessage } from '@/api/auth-channel';
import { refreshSession } from '@/api/http';
import { tokenStore } from '@/api/token-store';
import type { AuthUser, LoginInput } from '@/api/types';
import { authApi } from './api';
import { AuthContext, type AuthContextValue, type AuthStatus, type SignOutReason } from './auth-context';

// Refresh this long before the access token expires.
const REFRESH_AHEAD_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [signOutReason, setSignOutReason] = useState<SignOutReason>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    const delay = Math.max(tokenStore.remainingMs() - REFRESH_AHEAD_MS, 5_000);
    refreshTimer.current = setTimeout(() => {
      // Failure is handled by the store listener below (session cleared).
      refreshSession().catch(() => tokenStore.clear('expired'));
    }, delay);
  }, []);

  // Keep React state in step with the token store (login, refresh, expiry).
  useEffect(
    () =>
      tokenStore.subscribe((event) => {
        if (event.type === 'session') {
          setUser(event.user);
          setSignOutReason(null);
          setStatus('authenticated');
          scheduleRefresh();
          return;
        }
        clearTimeout(refreshTimer.current);
        setUser(null);
        setSignOutReason(event.reason);
        setStatus('anonymous');
        queryClient.clear();
        if (event.reason === 'expired') {
          enqueueSnackbar('Your session has ended. Please sign in again.', { variant: 'info' });
        }
      }),
    [queryClient, enqueueSnackbar, scheduleRefresh],
  );

  // Other tabs: adopt their new session, follow their logout.
  useEffect(
    () =>
      onAuthMessage((message) => {
        if (message.type === 'logout') {
          tokenStore.clear('logout');
          return;
        }
        tokenStore.set(message.session, message.issuedAt);
      }),
    [],
  );

  // Restore the session from the refresh cookie on page load.
  useEffect(() => {
    let cancelled = false;
    refreshSession().catch(() => {
      if (!cancelled) setStatus('anonymous');
    });
    return () => {
      cancelled = true;
      clearTimeout(refreshTimer.current);
    };
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const session = await authApi.login(input);
    tokenStore.set(session);
    broadcastAuth({ type: 'session', session, issuedAt: Date.now() });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      tokenStore.clear('logout');
      broadcastAuth({ type: 'logout' });
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signOutReason, login, logout }),
    [status, user, signOutReason, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
